import {
  Map,
  Marker,
  Popup,
  AttributionControl,
  NavigationControl,
} from 'maplibre-gl';
import { t } from 'javascripts/utils/withTranslation';
import CopyrightInteractive from '../../atoms/copyright/copyright';

function insertOsmMapCss() {
  return new Promise((resolve) => {
    const $link = document.createElement('link');
    $link.rel = 'stylesheet';
    $link.href = `${window.bawue2024.ASSET_BASE_URL}stylesheets/vendors/osm_maplibre/maplibre-gl.css`;

    document.head.appendChild($link);
    $link.onload = () => {
      resolve();
    };
  });
}

insertOsmMapCss().then(() => {
  document.querySelectorAll('[data-osm-map]').forEach(($map) => {
    const $mapJson = $map.dataset.osmJson || null;
    const $mapLng = parseFloat($map.dataset.osmCenterLng || 9.983333);
    const $mapLat = parseFloat($map.dataset.osmCenterLat || 48.400002);
    const $mapZoom = parseInt($map.dataset.osmZoom || 6, 10);
    const $mapZoomMax = parseInt($map.dataset.osmZoomMax || 22, 10);
    const $mapZoomMin = parseInt($map.dataset.osmZoomMin || 3, 10);
    const $mapPopupClasses = $map.dataset.osmPopupClasses || '';

    if ($mapJson !== null) {
      try {
        fetch(
          `${window.bawue2024.ASSET_BASE_URL}javascripts/vendor/osm_maplibre/style_current.json`
        )
          .then(($stylesDefault) => $stylesDefault.json())
          .then(($stylesDefault) => {
            const $stylesOverrides = {
              glyphs: `${window.bawue2024.ASSET_BASE_URL}javascripts/vendor/osm_maplibre/fonts/{fontstack}/{range}.pbf`,
            };
            const $stylesFinal = { ...$stylesDefault, ...$stylesOverrides };

            const map = new Map({
              container: $map,
              // see: https://cloud.maptiler.com/account/keys/ for styles
              // style: `${window.bawue2024.ASSET_BASE_URL}javascripts/vendor/osm_maplibre/style_current.json`,
              style: $stylesFinal,
              center: [$mapLng, $mapLat],
              zoom: $mapZoom,
              minZoom: $mapZoomMin,
              maxZoom: $mapZoomMax,
              attributionControl: false,
              // cooperativeGestures: true,
              cooperativeGestures: {
                windowsHelpText: t(
                  'Verwenden Sie STRG + Scrollen zum Zoomen der Karte'
                ),
                macHelpText: t(
                  'Verwenden Sie ⌘ + Scrollen zum Zoomen der Karte'
                ),
              },
            });

            map.on('error', (e) => {
              const $parent = $map.parentNode;
              $parent.classList.add('map--error');
              const newEl = document.createElement('div');
              newEl.classList.add('map__error');
              newEl.innerHTML = t(
                'Die Karte konnte nicht geladen werden. Bitte versuchen Sie es später erneut.'
              );
              $parent.replaceChild(newEl, $map);
              map.remove();
              console.error('test', e);
            });

            map.on('load', () => {
              map.addSource('locations', {
                type: 'geojson',
                data: $mapJson,
                cluster: true,
                clusterMaxZoom: 9,
                clusterRadius: 50,
              });

              map.addControl(
                new AttributionControl({
                  customAttribution:
                    '<a href="https://www.openstreetmap.org/copyright" target="_blank">&#169; OpenStreetMap contributors</a>',
                  compact: false,
                })
              );
              map.addControl(
                new NavigationControl({ showCompass: false }),
                'bottom-right'
              );
              // example how to enable fullscreen button
              // map.addControl(new FullscreenControl({container: document.querySelector('body')}));

              function circleColor() {
                return $map.dataset.osmClusterBg || '#736759';
              }

              function textColor() {
                return $map.dataset.osmClusterColor || '#fff';
              }

              map.addLayer({
                id: 'clusters',
                type: 'circle',
                source: 'locations',
                filter: ['has', 'point_count'],
                paint: {
                  'circle-color': circleColor(),
                  'circle-radius': 20,
                },
              });

              map.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: 'locations',
                filter: ['has', 'point_count'],
                layout: {
                  'text-field': '{point_count_abbreviated}',
                  'text-font': ['Open Sans Regular'],
                  'text-size': 18,
                },
                paint: {
                  'text-color': textColor(),
                },
              });

              // inspect a cluster on click
              map.on('click', 'clusters', async (e) => {
                const features = map.queryRenderedFeatures(e.point, {
                  layers: ['clusters'],
                });
                const clusterId = features[0].properties.cluster_id;
                const zoom = await map
                  .getSource('locations')
                  .getClusterExpansionZoom(clusterId);
                map.easeTo({
                  center: features[0].geometry.coordinates,
                  zoom,
                });
              });

              map.on('mouseenter', 'clusters', () => {
                map.getCanvas().style.cursor = 'pointer';
              });

              map.on('mouseleave', 'clusters', () => {
                map.getCanvas().style.cursor = '';
              });

              // ---- individual part begins

              const markers = {};
              let markersOnScreen = {};

              function createIconMarker(props) {
                const $icon = props.markerIcon
                  ? JSON.parse(props.markerIcon)
                  : false;
                if ($icon && $icon.image) {
                  const el = document.createElement('div');
                  el.innerHTML = `<div style="cursor: pointer;width:${$icon.image_size_x}px;height:${$icon.image_size_y}ppx">
                    <img src="${$icon.image}" style="width:${$icon.image_size_x}px;height:${$icon.image_size_y}px">
                    </div>`;

                  return el.firstChild;
                }
                return null;
              }

              function updateMarkers() {
                const newMarkers = {};
                const features = map.querySourceFeatures('locations');

                // eslint-disable-next-line no-restricted-syntax
                for (const feature of features) {
                  const coords = feature.geometry.coordinates;
                  const props = feature.properties;

                  // eslint-disable-next-line no-continue
                  if (props.cluster) continue;

                  const { id } = props;
                  let marker = markers[id];
                  if (!marker) {
                    const el = createIconMarker(props);
                    const markerTitle = props.title;
                    const markerText = props.text;
                    const markerLink = props.linktext || '';
                    const copyright = props.imagecopyright;
                    let markerCopyright = '';

                    if (copyright) {
                      markerCopyright = `<div class="figure__copyright map__copyright">
                        <div class="copyright"><svg class="icon icon--copyright copyright__icon" viewBox="0 0 200 200" role="img" aria-label="Copyright"><title>Copyright</title><use xlink:href="#icon-copyright"></use></svg>
                        <span class="copyright__text">${copyright}</span></div></div></div>`;
                    }

                    const markerImageString = props.image
                      ? `<figure class="figure map__image">
                        <img src="${props.image}" class="map__image-img">
                        ${markerCopyright}
                    </figure>`
                      : `<br>`;
                    const $markerTextBlock = `${markerImageString}
                    <div class="map__text">
                        <h3 class="headline headline--5 map__headline">${markerTitle}</h3>
                        <div class="text text--small">${markerText}${markerLink}</div>
                    </div>`;

                    const popup = new Popup({
                      maxWidth: '400px',
                      className: `map__overlay ${$mapPopupClasses}`,
                      focusAfterOpen: false,
                    })
                      .setLngLat(coords)
                      .setHTML($markerTextBlock)
                      .addTo(map);

                    if (copyright) {
                      popup.on('open', ({ target }) => {
                        // eslint-disable-next-line no-underscore-dangle
                        const $popup = target?._container;
                        const cop = $popup.querySelector('.copyright');
                        if (
                          !cop?.classList.contains('copyright--interactive')
                        ) {
                          return new CopyrightInteractive(cop);
                        }
                        return null;
                      });
                    }

                    marker = new Marker({
                      // anchor: 'bottom-left',
                      element: el,
                    })
                      .setLngLat(coords)
                      .setPopup(popup);
                    markers[id] = marker;
                  }

                  /** Example pan to fit popup
                     * See: https://github.com/mapbox/mapbox-gl-js/issues/3325

                     const popup = marker.getPopup();

                     popup.on('open', () => {
                     const point = map.project(popup._lngLat);
                     const popupHeight = popup._container.clientHeight + 50; // Small amount of padding
                     if (point.y - popupHeight < 0) {
                     map.panBy([0, point.y - popupHeight]);
                     }
                     })
                     */

                  newMarkers[id] = marker;

                  if (!markersOnScreen[id]) marker.addTo(map);
                }
                // for every marker we've added previously, remove those that are no longer visible
                // eslint-disable-next-line no-restricted-syntax
                for (const id in markersOnScreen) {
                  if (!newMarkers[id]) markersOnScreen[id].remove();
                }

                markersOnScreen = newMarkers;
              }

              // ---- individual part ends

              // after the GeoJSON data is loaded, update markers on the screen on every frame
              map.on('render', () => {
                if (!map.isSourceLoaded('locations')) return;
                updateMarkers();
              });
            });
          });
      } catch (e) {
        console.error(e);
      }
    }
  });
});
